import { ContractItem } from '@/api';
import { assureurs } from '@/constants/grilleAssureursData';
import normalizeUrl from '@/utils/functions/normalizeUrl/normalizeUrl';

type CatalogEntry = (typeof assureurs)[number];

interface PartnerCard {
  id: string;
  logo: string;
  label: string;
  assureur: string;
  link: string;
  modaleContrats: string[];
}

interface UnmatchedGestionnaire {
  gestionnaire: string;
  gestionnaireAC: string;
}

interface GestionnairesCatalog {
  gestionnaires: PartnerCard[];
  unmatched: UnmatchedGestionnaire[];
}

interface GestionnaireGroup {
  id: string;
  order: number;
  entry: CatalogEntry;
  gestionnaire: string;
  logo: string;
  produits: string[];
}

const toCatalogCard = ({ logo, label, assureur, link, modaleContrats }: CatalogEntry): PartnerCard => ({
  id: assureur,
  logo,
  label,
  assureur,
  link,
  modaleContrats,
});

const filterGestionnairesCatalog = (
  catalog: CatalogEntry[],
  contrats: ContractItem[],
  isDemo: boolean,
  formatLabel: (gestionnaire: string) => string
): GestionnairesCatalog => {
  if (isDemo || contrats.length === 0) return { gestionnaires: catalog.map(toCatalogCard), unmatched: [] };

  const groupsByGestionnaire = new Map<string, GestionnaireGroup>();
  const unmatchedByKey = new Map<string, UnmatchedGestionnaire>();

  contrats.forEach((contrat) => {
    const contratUrl = normalizeUrl(contrat.gestionnaireAC);
    if (!contratUrl) return;

    const order = catalog.findIndex((assureur) => normalizeUrl(assureur.link) === contratUrl);
    const entry = catalog[order];

    if (!entry) {
      const gestionnaire = contrat.gestionnaire ?? '';
      unmatchedByKey.set(`${contratUrl}|${gestionnaire}`, {
        gestionnaire,
        gestionnaireAC: contrat.gestionnaireAC ?? '',
      });
      return;
    }

    const gestionnaire = contrat.gestionnaire?.trim() || entry.assureur;
    const id = `${order}|${contrat.idGestionnaire?.trim() || gestionnaire}`;
    const produit = contrat.nomProduit?.trim();
    const group = groupsByGestionnaire.get(id);

    if (group) {
      if (produit && !group.produits.includes(produit)) group.produits.push(produit);
      return;
    }

    groupsByGestionnaire.set(id, {
      id,
      order,
      entry,
      gestionnaire,
      logo: contrat.gestionnaireLogo?.trim() ?? '',
      produits: produit ? [produit] : [],
    });
  });

  const gestionnaires = [...groupsByGestionnaire.values()]
    .sort((a, b) => a.order - b.order)
    .map(({ id, entry, gestionnaire, logo, produits }) => ({
      id,
      logo: logo || entry.logo,
      label: formatLabel(gestionnaire),
      assureur: gestionnaire,
      link: entry.link,
      modaleContrats: produits.length > 0 ? produits : entry.modaleContrats,
    }));

  return { gestionnaires, unmatched: [...unmatchedByKey.values()] };
};

export { filterGestionnairesCatalog };
export type { GestionnairesCatalog, PartnerCard, UnmatchedGestionnaire };
